import React, {
  useState, useCallback, useMemo, useRef, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { usePopperTooltip } from 'react-popper-tooltip';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import HeaderText from '../../../shared/components/HeaderText/HeaderText';
import Text from '../../../shared/components/Text/Text';

import InlineExerciseEditor from './InlineExerciseEditor';
import {
  resetImport,
  selectExerciseForMatch,
  setSelectedProgram,
  confirmImport,
  setLastImportedProgramId,
} from '../ducks';
import { LOW_CONFIDENCE_THRESHOLD } from '../ducks/actionTypes';
import { axiosAuthed } from '../../../shared/utils/setCommonHeaders';
import BaseModal from '../../calendar/modals/BaseModal';
import CreateNewComponentModal from '../../calendar/modals/CreateNewComponentModal';
import {
  setActiveModal,
  setExercisePickerSearchTerm,
  setProgramType,
  setSelectedReduxNonDateBasedProgram,
} from '../../calendar/ducks/calendarActions';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// The wide canvas gives the matched-to dropdown enough room to show long
// exercise names without truncating mid-word.
const OuterWrapper = styled('div')`
  max-width: 1100px;
  width: 100%;
  margin: 0 auto 96px;
  display: flex;
  flex-direction: column;
  background: #FFFFFF;
  border-radius: 16px;
  border: 1px solid #EEEEEE;
  box-shadow:
    0 0 1px 0 rgba(48, 49, 51, 0.05),
    0 16px 24px 0 rgba(48, 49, 51, 0.10);
  animation: ${fadeUp} 360ms cubic-bezier(0.2, 0.8, 0.2, 1) both;

  @media (max-width: 768px) {
    margin: 0 auto 32px;
    border-radius: 12px;
  }
`;

// The action bar and table step out of this padded section because they
// span the full card.
const HeaderBlock = styled('div')`
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 768px) {
    padding: 20px;
    gap: 24px;
  }
`;

const TitleBlock = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

const Title = styled(HeaderText)`
  font-size: 24px;
  font-weight: 900;
  margin: 0;
  letter-spacing: 0;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const ProgramNameField = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  max-width: 600px;
`;

// `margin: 0 !important` defeats the project's global
// `label { margin-bottom: 0.5rem }` reset.
const ProgramNameLabel = styled('label')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0;
  color: #444444;
  line-height: 1.5;
  margin: 0 !important;
  display: block;
`;

// Right padding leaves room for a future trailing icon.
const ProgramNameInput = styled('input')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #444444;
  letter-spacing: 0;
  line-height: 1.5;
  padding: 8px 56px 8px 16px;
  border: 2px solid #E0E0E0;
  border-radius: 4px;
  background: #FFFFFF;
  outline: none;
  transition: border-color 120ms ease, box-shadow 120ms ease;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: #C9C9D4;
    font-weight: 400;
  }

  &:hover {
    border-color: #C9C9D4;
  }

  &:focus,
  &:focus-visible {
    border-color: #5b86e5;
    box-shadow: 0 0 0 3px rgba(91, 134, 229, 0.16);
  }
`;

const ExercisesBlock = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  color: #444444;
  font-size: 16px;
  line-height: 1.5;
  max-width: 676px;
`;

const ExercisesIntro = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const ExercisesHeading = styled(HeaderText)`
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  line-height: 1.5;
`;

const ExercisesBody = styled(Text)`
  font-size: 16px;
  font-weight: 400;
  margin: 0;
  line-height: 1.5;
`;

const ErrorBar = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 32px 16px;
  padding: 12px 14px;
  background: #FEF3F2;
  border: 1px solid #F5CECA;
  border-radius: 8px;
  animation: ${fadeUp} 180ms ease both;
`;

const ErrorText = styled('div')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #9A2420;
  line-height: 1.45;
`;

const RetryButton = styled('button')`
  margin-left: auto;
  flex-shrink: 0;
  align-self: center;
  border: 1px solid #F5CECA;
  background: #FFFFFF;
  color: #9A2420;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  border-radius: 6px;
  padding: 5px 12px;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: #FEE4E2;
  }
`;

const TableWrap = styled('div')`
  border-top: 1px solid #EEEEEE;
`;

// Col 2 (matched-to dropdown) uses minmax so typical rows read as a fixed
// 320px, but genuinely long matched names can expand to 480px before ellipsis.
const GRID_COLS = '220px minmax(320px, 480px) 90px 100px';

const ListHeader = styled('div')`
  display: grid;
  grid-template-columns: ${GRID_COLS};
  align-items: center;
  gap: 16px;
  padding: 8px 32px;
  background: #FAFAFD;
  border-bottom: 1px solid #EEEEEE;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
  color: #9E9E9E;
  line-height: 1.5;
  user-select: none;

  /* Mobile: rows stack vertically and the table header would just clutter,
     so we hide it. Each row labels itself with a "Detected" tag at the top
     of the source name (see Row styles below). */
  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderLabel = styled('span')`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderLabelRight = styled(HeaderLabel)`
  text-align: right;
`;

const List = styled('ul')`
  list-style: none;
  margin: 0;
  padding: 0;
`;

// needsAttention (unresolved or low-confidence match) gets a yellow wash;
// danger red is reserved for true error states (e.g. the ErrorBar above).
const Row = styled(({
  isActive, isFlashing, needsAttention, ...rest
}) => <li {...rest} />)`
  position: relative;
  display: grid;
  grid-template-columns: ${GRID_COLS};
  align-items: center;
  gap: 16px;
  padding: 16px 32px;
  border-bottom: 1px solid #EEEEEE;
  cursor: pointer;
  background: ${(props) => {
    if (props.isFlashing) return '#FFECA0';
    if (props.isActive) return '#FAFAFC';
    if (props.needsAttention) return 'rgba(255, 224, 145, 0.1)';
    return 'transparent';
  }};
  transition: background 900ms ease;

  &:last-of-type {
    border-bottom: none;
  }

  &:hover {
    background: ${(props) => {
    if (props.isFlashing) return '#FFECA0';
    if (props.isActive) return '#FAFAFC';
    if (props.needsAttention) return 'rgba(255, 224, 145, 0.18)';
    return '#FCFCFD';
  }};
  }

  /* Mobile: collapse the 4-column grid into a single-column stack so each
     row reads as a small card: detected name on top, matched-to dropdown
     full-width, then PREVIEW + status side-by-side at the bottom. */
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 16px 20px;
  }
`;

// Left cell holds the source (extracted) name. The row-level yellow tint
// now carries the "needs attention" signal, so we don't render a left-edge
// status dot — the badge column on the right holds the Corrected / Check
// this indicator.
const SourceCell = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const ExtractedName = styled('span')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #444444;
  letter-spacing: 0;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const MatchedCell = styled(({ unresolved, isActive, ...rest }) => <div {...rest} />)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
  position: relative;
  background: #FFFFFF;
  border: 1px solid ${(props) => (props.isActive ? '#444444' : '#E0E0E0')};
  border-radius: 4px;
  padding: 4px 8px;
  /* Locked height so swapping between the static MatchedName view and the
     active InlineExerciseEditor doesn't change the row height (the editor's
     Select control is 34px; the static label is ~24px). Centring keeps both
     vertically centered in the same box, so the row below never shifts when
     the coach clicks into a dropdown. */
  min-height: 40px;
  height: 40px;
  box-sizing: border-box;
  transition: border-color 140ms ease;

  &:hover {
    border-color: #C9C9D4;
  }
`;

// Inline SVG so it inherits color from the parent and doesn't require an
// asset import.
const MatchedChevron = styled('span')`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #444444;
  pointer-events: none;
`;

// Single-line with ellipsis; cell is height-locked (see MatchedCell) so the
// row doesn't grow/shrink as the coach clicks into the dropdown.
const MatchedName = styled(({ unresolved, ...rest }) => <span {...rest} />)`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: ${(props) => (props.unresolved ? '#9E9E9E' : '#444444')};
  font-style: ${(props) => (props.unresolved ? 'italic' : 'normal')};
  letter-spacing: 0;
  line-height: 1.5;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// The PREVIEW button itself comes from ExerciseInfoPreview; this is just the
// alignment slot.
const PreviewCell = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
`;

// Left-aligned so the warning-triangle icon sits at a consistent x-position
// (col 4's left edge) across every row, regardless of whether the
// neighboring Preview cell is populated or empty. Right-aligning here made
// "NO MATCH" rows (which skip the Preview button) float their badge far
// right with no anchor on the left — the icons no longer formed a vertical
// line down the column.
const ConfidenceCell = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
`;

const flashPopIn = keyframes`
  from { opacity: 0; transform: translateX(-4px); }
  to   { opacity: 1; transform: translateX(0); }
`;

// "CORRECTED" indicator. Only renders for rows that were originally
// needs-attention and have since been corrected.
const UpdatedBadge = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
  padding: 6px 0;
  color: #0DCC8A;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.36px;
  line-height: 1.5;
  text-transform: uppercase;
  white-space: nowrap;
  animation: ${flashPopIn} 220ms cubic-bezier(0.2, 0.8, 0.2, 1) both;

  &::before {
    content: '';
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'><path d='M3 8.5 L6.5 12 L13 4.5' stroke='%230DCC8A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>");
    background-repeat: no-repeat;
  }
`;

// Eye-icon preview trigger next to the matched exercise name. Hovering
// reveals the popover; clicking must not open the row editor, so the onClick
// stops propagation in the JSX. Built on a plain `button` so emotion forwards
// the DOM ref natively (react-popper-tooltip needs it for positioning); the
// `isOpen` style prop is filtered out of the DOM via shouldForwardProp.
const InfoIconButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: ${(props) => (props.isOpen ? '#F4F4F6' : 'transparent')};
  color: ${(props) => (props.isOpen ? '#444444' : '#9E9E9E')};
  cursor: pointer;
  transition: color 140ms ease, background 140ms ease;

  &:hover {
    color: #444444;
    background: #F4F4F6;
  }

  &:focus-visible {
    outline: 2px solid #5b86e5;
    outline-offset: 2px;
  }
`;

// Kept very short and opacity-only so rapid hovers between rows don't read
// as a "flash" — the popover appears almost instantly while still feeling
// like it's fading rather than snapping.
const popoverFadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// Outer wrapper that owns the popper-positioned coordinates. The arrow sits
// here, *outside* InfoPopover, so InfoPopover can keep `overflow: hidden`
// for rounded-corner clipping of the video without also clipping the arrow.
// Portaled to document.body so ancestor overflow/transform contexts can't
// clip it either.
const PopoverPositioner = styled('div')`
  z-index: 50;
  position: relative;
  animation: ${popoverFadeIn} 80ms ease-out both;
`;

// Floating detail card. Positioning, flip, and shift are owned by Popper via
// react-popper-tooltip (see ExerciseInfoPreview below) — this styled element
// only describes the visual shell.
const InfoPopover = styled('div')`
  width: 340px;
  max-width: calc(100vw - 48px);
  background: #FFFFFF;
  border: 1px solid #E7E7EE;
  border-radius: 12px;
  box-shadow:
    0 2px 6px rgba(16, 18, 40, 0.06),
    0 24px 48px -12px rgba(16, 18, 40, 0.22);
  overflow: hidden;
  cursor: default;
`;

// Arrow positioned by Popper's arrow modifier (getArrowProps), which sets
// inline left/top on this element so the arrow tracks the trigger center.
// We use a CSS-triangle ::after (no fill div, no rotation) and a matching
// ::before one pixel further out for the border color, so the arrow looks
// like a continuous extension of InfoPopover's border. Margin pulls the
// arrow out of the popper container (negative top/bottom/left/right) —
// using `margin-*` instead of `top/bottom/left/right` so Popper's inline
// position styles aren't overridden.
const PopoverArrow = styled('div')`
  position: absolute;
  width: 10px;
  height: 10px;
  pointer-events: none;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
  }

  [data-popper-placement^='bottom'] > & {
    margin-top: -5px;
  }
  [data-popper-placement^='bottom'] > &::before {
    border-color: transparent transparent #E7E7EE transparent;
    border-width: 0 5px 5px 5px;
  }
  [data-popper-placement^='bottom'] > &::after {
    top: 1px;
    border-color: transparent transparent #FFFFFF transparent;
    border-width: 0 5px 5px 5px;
  }

  [data-popper-placement^='top'] > & {
    margin-bottom: -5px;
  }
  [data-popper-placement^='top'] > &::before {
    border-color: #E7E7EE transparent transparent transparent;
    border-width: 5px 5px 0 5px;
  }
  [data-popper-placement^='top'] > &::after {
    top: -1px;
    border-color: #FFFFFF transparent transparent transparent;
    border-width: 5px 5px 0 5px;
  }

  [data-popper-placement^='right'] > & {
    margin-left: -5px;
  }
  [data-popper-placement^='right'] > &::before {
    border-color: transparent #E7E7EE transparent transparent;
    border-width: 5px 5px 5px 0;
  }
  [data-popper-placement^='right'] > &::after {
    left: 1px;
    border-color: transparent #FFFFFF transparent transparent;
    border-width: 5px 5px 5px 0;
  }

  [data-popper-placement^='left'] > & {
    margin-right: -5px;
  }
  [data-popper-placement^='left'] > &::before {
    border-color: transparent transparent transparent #E7E7EE;
    border-width: 5px 0 5px 5px;
  }
  [data-popper-placement^='left'] > &::after {
    left: -1px;
    border-color: transparent transparent transparent #FFFFFF;
    border-width: 5px 0 5px 5px;
  }
`;

// Accepts either a YouTube thumbnail URL (rendered as background-image) or
// renders an inline <video> child for uploaded/hosted video files. Either
// way a gradient + play badge sits on top, and the whole tile links out to
// the source URL.
const PopoverVideo = styled('a')`
  display: block;
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #1A1A26 center/cover no-repeat;
  background-image: ${(props) => (props.thumb ? `url(${props.thumb})` : 'none')};
  cursor: pointer;
  text-decoration: none;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.35));
    z-index: 1;
  }
`;

// First-frame preview for uploaded video URLs (MP4 etc.). The #t=0.1 seek
// fragment nudges Safari/Chrome into rendering a real frame instead of a
// black box when preload='metadata' is set. Muted + playsInline avoids
// inline-play restrictions if anything triggers play.
const PopoverVideoEl = styled('video')`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #1A1A26;
  z-index: 0;
`;

// Wraps inline playback so both the YouTube iframe and the HTML5 player
// inherit the same 16:9 aspect ratio as the thumbnail tile — no layout
// jump between states.
const PlayingSlot = styled('div')`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000000;
  overflow: hidden;
`;

const PopoverIframe = styled('iframe')`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
`;

const PopoverPlayingVideo = styled('video')`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000000;
`;

const PlayBadge = styled('span')`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
  z-index: 2;

  &::after {
    content: '';
    width: 0;
    height: 0;
    margin-left: 3px;
    border-top: 7px solid transparent;
    border-bottom: 7px solid transparent;
    border-left: 12px solid #14162B;
  }
`;

const shimmer = keyframes`
  0%   { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

// Dark placeholder shown in the popover's video slot while we're fetching
// the detail record (before we know whether a video exists). Avoids a
// layout pop when the thumbnail lands.
const PopoverVideoSkeleton = styled('div')`
  width: 100%;
  aspect-ratio: 16 / 9;
  background: linear-gradient(
    90deg,
    #1A1A26 0%,
    #242434 50%,
    #1A1A26 100%
  );
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s linear infinite;
`;

const PopoverBody = styled('div')`
  padding: 14px 16px 14px;
`;

const PopoverDescription = styled(Text)`
  font-size: 13px;
  font-weight: 500;
  color: #3D3F55;
  margin: 10px 0 0;
  line-height: 1.5;
  /* Cap to ~4 lines so long descriptions don't dominate the card. */
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TagList = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
`;

const TagChip = styled('span')`
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  height: 18px;
  border-radius: 9px;
  background: #F2F2F7;
  color: #5D5F76;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PopoverHead = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PopoverName = styled(HeaderText)`
  font-size: 15px;
  font-weight: 800;
  color: #14162B;
  margin: 0;
  line-height: 1.25;
  letter-spacing: -0.2px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Type tone depends on the exercise category so each pill reads as a little
// identity tag rather than a repetitive green chip.
const TYPE_TONES = {
  L: { bg: '#E4F6ED', fg: '#0F7A43' }, // Lift — green
  R: { bg: '#E8F1FE', fg: '#1F5FBF' }, // SAQ+C — blue
  C: { bg: '#F3ECFB', fg: '#6A3FBF' }, // Circuit — purple
  W: { bg: '#FFF4E1', fg: '#9A6300' }, // Warm-up — amber
  CD: { bg: '#EAF4F2', fg: '#1F6B60' }, // Cooldown — teal
};

const TypeChip = styled(({ tone, ...rest }) => <span {...rest} />)`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 0 10px;
  height: 22px;
  border-radius: 11px;
  background: ${(props) => props.tone.bg};
  color: ${(props) => props.tone.fg};
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.2px;
`;

const DesignationRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

// When the value is "None" or "N/A" the value reads in a muted tone so the
// field still scans as "no max set" at a glance.
const MaxInfoBadge = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px;
  height: 22px;
  border-radius: 11px;
  background: #F2F2F7;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1px;
  color: #2B2B3A;
`;

const MaxInfoLabel = styled('span')`
  color: #8A8BA0;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  font-size: 9px;
`;

const MaxInfoValue = styled(({ muted, ...rest }) => <span {...rest} />)`
  color: ${(props) => (props.muted ? '#9A9AAE' : '#2B2B3A')};
  font-style: ${(props) => (props.muted ? 'italic' : 'normal')};
`;

// Optional section below the header — only rendered when we have something
// meaningful to put in it (linked exercise, default dose, tags). Skipping
// "None"/"—" filler rows keeps the card honest when an exercise is bare.
const MetaBlock = styled('div')`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #F0F0F5;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetaRow = styled('div')`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

const MetaKey = styled('span')`
  flex-shrink: 0;
  width: 72px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  color: #9A9AAE;
`;

const MetaValue = styled('span')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #2B2B3A;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Falls-through message when the exercise record is bare (no video, no
// linked exercise, no tracking text, no tags). Better than an empty card or
// a row of "None"s.
const PopoverEmpty = styled(Text)`
  font-size: 12px;
  color: #9A9AAE;
  margin: 6px 0 0;
  line-height: 1.45;
  font-style: italic;
`;

const getTypeLabel = (type) => {
  switch (type) {
    case 'L': return 'Lift';
    case 'R': return 'SAQ+C';
    case 'C': return 'Circuit';
    case 'W': return 'Warm-up';
    case 'CD': return 'Cooldown';
    default: return type || '';
  }
};

// "Max Info" is the coach-facing label for the lift-PR tracking config. The
// value is always populated so the popover can render the field consistently
// alongside the type chip:
//  - Lift: the configured max type ("Max Weight (1RM)", "Velocity", etc.)
//    falling back to "None" when the coach has explicitly disabled tracking.
//  - Anything else (SAQ+C, Circuit, Warm-up, Cooldown): "N/A" because max
//    tracking isn't a concept for those exercise types.
const formatMaxInfo = (ex) => {
  if (!ex || !ex.type) return 'N/A';
  if (ex.type !== 'L') return 'N/A';
  if (ex.defaultOptions?.barSpeed) return 'Velocity';
  switch (ex.track) {
    case 1: return 'Max Weight (1RM)';
    case 2: return 'Highest Reps';
    case 3: return '3RM';
    case 4: return 'Highest Weight';
    case 0: return 'None';
    default: return 'None';
  }
};

// Surfaces the Default Settings booleans from the "About Item" modal as
// readable chips. Mirrors the checkboxes the coach sees in the full
// Create/Edit Exercise form. Order matches the modal's visual order so the
// popover reads as a preview of that modal. Any that are falsy get dropped.
// Mirrors the camelCased keys the backend emits under `defaultOptions` (see
// node-server converters.js convertToDbDefaultLiftOptions / SAQC / Circuit).
// Top-level fallbacks cover Circuit's top-level `coachCompletion` and the
// frontend's own cached shape for freshly-created exercises.
const getTrackingFlags = (ex) => {
  if (!ex) return [];
  const out = [];
  const opts = ex.defaultOptions || {};
  if (opts.liftComp || opts.cardioComp || ex.completionOnly) out.push('Completion Only');
  if (opts.liftCoachComp || opts.cardioCoach || opts.circuitCoach || ex.coachCompletion) out.push('Coach Completion');
  if (opts.liftRepsOnly) out.push('Bodyweight');
  if (opts.peakPower) out.push('Peak Power');
  if (opts.barSpeed) out.push('Velocity');
  if (opts.eachSide) out.push('Each Side');
  if (opts.trackVolLoad) out.push('Volume Load');
  if (opts.trackRepCount) out.push('Rep Count');
  return out;
};

// Pull an 11-char video id from common YouTube URL shapes. Used both for
// derived thumbnails and the in-popover embed URL on click.
const getYoutubeId = (url) => {
  if (!url) return null;
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([A-Za-z0-9_-]{11})/,
  );
  return match ? match[1] : null;
};

const getYoutubeThumb = (url) => {
  const id = getYoutubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : null;
};

// "NO MATCH" indicator — no selectedExerciseId, which blocks IMPORT PROGRAM.
const NoMatchPill = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  gap: 8px;
  padding: 6px 0;
  color: #FF0505;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.36px;
  line-height: 1.5;
  text-transform: uppercase;
  white-space: nowrap;

  &::before {
    content: '';
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'><path d='M8 1.33333 L14.6667 13.3333 H1.33333 Z' fill='%23FF0505'/><line x1='8' y1='6' x2='8' y2='9' stroke='white' stroke-width='1.6' stroke-linecap='round'/><circle cx='8' cy='11' r='0.8' fill='white'/></svg>");
    background-repeat: no-repeat;
  }
`;

const CheckThisPill = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  gap: 8px;
  padding: 6px 0;
  color: #FFCB47;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.36px;
  line-height: 1.5;
  text-transform: uppercase;
  white-space: nowrap;

  &::before {
    content: '';
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'><path d='M8 1.33333 L14.6667 13.3333 H1.33333 Z' stroke='%23FFCB47' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' fill='none'/><line x1='8' y1='6' x2='8' y2='9' stroke='%23FFCB47' stroke-width='1.6' stroke-linecap='round'/><circle cx='8' cy='11' r='0.8' fill='%23FFCB47'/></svg>");
    background-repeat: no-repeat;
  }
`;

const ActionBar = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px;
  background: #FFFFFF;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;

  @media (max-width: 768px) {
    padding: 12px 20px;

    > button {
      flex: 1;
    }
  }
`;

const CancelButton = styled('button')`
  padding: 6px 16px;
  background: transparent;
  color: #444444;
  border: 2px solid #EEEEEE;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.39px;
  line-height: 1.5;
  cursor: pointer;
  transition: all 160ms ease;

  &:hover {
    border-color: #C9C9D4;
    background: #FAFAFC;
  }
`;

const PrimaryButton = styled('button')`
  padding: 6px 16px;
  background: ${(props) => (props.disabled ? '#E0E0E0' : '#14162B')};
  color: ${(props) => (props.disabled ? '#757575' : '#FFFFFF')};
  border: none;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.39px;
  line-height: 1.5;
  text-transform: uppercase;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background 160ms ease, transform 160ms ease, box-shadow 160ms ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: #000014;
    box-shadow: 0 6px 18px rgba(20, 22, 43, 0.28);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(20, 22, 43, 0.2);
  }
`;

const ArrowGlyph = styled('span')`
  font-size: 14px;
  line-height: 1;
  transition: transform 180ms ease;

  ${PrimaryButton}:hover:not(:disabled) & {
    transform: translateX(2px);
  }
`;

// Mirrors the width: 100% wrapper CalendarModalHandler renders around the
// CreateNewComponentModal. BaseModal's content area is a flexbox; without an
// explicit width here the inner modal collapses to content size and the
// form's ResizeObserver picks up a narrow value — which trips the Circuit /
// SAQ+C 528px breakpoint into single-column layout.
const CreateModalWrapper = styled('div')`
  width: 100%;
`;

// Per-row preview trigger + popover. Lives in its own component so we can
// call usePopperTooltip once per row (hook rules — can't call inside a map
// callback in the parent). Open/close stays parent-controlled via `isOpen`
// so only one popover shows at a time and so we can drive the lazy
// detail-fetch from the same place as before.
const ExerciseInfoPreview = ({
  isOpen,
  onOpen,
  onClose,
  onToggleClick,
  children,
}) => {
  const {
    getArrowProps,
    getTooltipProps,
    setTooltipRef,
    setTriggerRef,
  } = usePopperTooltip({
    trigger: null,
    visible: isOpen,
    placement: 'bottom',
    offset: [0, 10],
    interactive: true,
  });

  return (
    <>
      <InfoIconButton
        ref={setTriggerRef}
        type='button'
        isOpen={isOpen}
        aria-label='Preview exercise details'
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        onFocus={onOpen}
        onBlur={onClose}
        onClick={onToggleClick}
      >
        <svg width='24' height='24' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
          <path
            d='M3 12 C5 7.6 8.2 5.5 12 5.5 C15.8 5.5 19 7.6 21 12 C19 16.4 15.8 18.5 12 18.5 C8.2 18.5 5 16.4 3 12 Z'
            stroke='currentColor'
            strokeWidth='1.6'
            strokeLinejoin='round'
          />
          <circle cx='12' cy='12' r='2.5' stroke='currentColor' strokeWidth='1.6' />
        </svg>
      </InfoIconButton>
      {isOpen && createPortal(
        <PopoverPositioner
          ref={setTooltipRef}
          {...getTooltipProps()}
          onMouseEnter={onOpen}
          onMouseLeave={onClose}
          onClick={(e) => e.stopPropagation()}
        >
          <InfoPopover>{children}</InfoPopover>
          <PopoverArrow {...getArrowProps()} />
        </PopoverPositioner>,
        document.body,
      )}
    </>
  );
};

ExerciseInfoPreview.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onToggleClick: PropTypes.func.isRequired,
  children: PropTypes.node,
};

ExerciseInfoPreview.defaultProps = {
  children: null,
};

// `flags` is injected by withLDConsumer at the bottom of this file. We
// only read `flags.aiImportHomeLanding` — gates whether the post-import
// flow lands the coach in the new Home Navigator (with a success banner)
// or in Build (with a toast). Default OFF until the new Home ships.
const StepExerciseChecker = ({ flags }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const matches = useSelector((state) => state.aiProgramImport.matches || []);
  const selectedProgram = useSelector((state) => state.aiProgramImport.selectedProgram);
  const isConfirming = useSelector((state) => state.aiProgramImport.isConfirming);
  const confirmError = useSelector((state) => state.aiProgramImport.confirmError);
  // Optional-chain through the auth slice — it hydrates async after app
  // boot, so a coach landing directly on Step 4 via the resume flow can
  // mount this component before currentUser exists.
  const accountCode = useSelector((state) => state.auth?.data?.currentUser?.accountCode);

  const [activeEditKey, setActiveEditKey] = useState(null);
  // The extracted-name row that opened the create-exercise modal. Used to
  // route the just-created exercise back to the correct row on success.
  const [createModalKey, setCreateModalKey] = useState(null);
  // True while the create form's similar-exercises dialog is open; hides the
  // create modal underneath so the two don't stack.
  const [isSimilarCheckOpen, setIsSimilarCheckOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  // Local draft for the editable program name. We keep it local so typing
  // doesn't churn redux on every keystroke; commit happens on blur / Enter
  // and on confirm.
  const [programNameDraft, setProgramNameDraft] = useState(selectedProgram || '');
  // Persistent per-row update counter. Presence = row has been modified at
  // least once (persists the yellow background + "Updated" pill). The counter
  // value is used as a React key on the pill so the animation re-plays on
  // every subsequent update.
  const [updateCountByKey, setUpdateCountByKey] = useState(() => new Map());
  // Transient per-row set. Row is in this set only while the brighter pulse
  // animation is playing (~700ms after each update).
  const [flashingKeys, setFlashingKeys] = useState(() => new Set());
  const flashTimeoutsRef = useRef({});

  const exerciseOptionsRef = useRef(null);
  const [exerciseOptions, setExerciseOptions] = useState([]);
  // Map<exerciseId, exerciseObject> — the lightweight list record used for
  // matching (id, name, type, track, inputType, linkTo, linkPercentage). The
  // Select dropdown uses the `{value,label}` array separately so the big
  // menu doesn't re-render when details come in.
  const [exerciseById, setExerciseById] = useState(() => new Map());
  // Map<exerciseId, detailObject> — lazy-filled on first hover of the info
  // icon for a given row. This is where video URL, description, tags, and
  // full defaultOptions come from; the list endpoint doesn't include them.
  const [exerciseDetailById, setExerciseDetailById] = useState(() => new Map());
  // Set<exerciseId> — in-flight detail fetches, used to show a loading state
  // in the popover and to dedupe concurrent hovers.
  const [fetchingDetailIds, setFetchingDetailIds] = useState(() => new Set());
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  // True when the /exercises fetch failed. Without this the dropdown would sit
  // permanently empty (the effect only re-runs on accountCode change) with no
  // feedback — the coach would have no way to recover short of a hard reload.
  const [optionsError, setOptionsError] = useState(false);
  const [hoveredInfoKey, setHoveredInfoKey] = useState(null);
  // The row whose video is currently playing inline inside the popover.
  // Reset whenever the hovered row changes so a different popover never
  // shows the previous row's player.
  const [playingVideoKey, setPlayingVideoKey] = useState(null);
  const hoverTimeoutRef = useRef(null);

  const loadExerciseOptions = useCallback(() => {
    if (!accountCode || exerciseOptionsRef.current) return;
    setIsLoadingOptions(true);
    setOptionsError(false);
    axiosAuthed.get(`/accounts/${accountCode}/exercises`)
      .then((res) => {
        const opts = res.data.map((ex) => ({ value: ex.id, label: ex.name }));
        exerciseOptionsRef.current = opts;
        setExerciseOptions(opts);
        const byId = new Map();
        res.data.forEach((ex) => byId.set(ex.id, ex));
        setExerciseById(byId);
        setIsLoadingOptions(false);
      })
      .catch(() => {
        setIsLoadingOptions(false);
        setOptionsError(true);
      });
  }, [accountCode]);

  useEffect(() => {
    loadExerciseOptions();
  }, [loadExerciseOptions]);

  // Lazy per-exercise detail fetch. Used both for on-hover lookups and the
  // background prefetch below. The list endpoint is lean and doesn't carry
  // video URLs / descriptions — we pull the full record on demand, cache it
  // for the session, and dedupe against in-flight requests. Preloads the
  // YouTube thumbnail on success so the hover state renders instantly.
  const fetchExerciseDetail = useCallback((exerciseId) => {
    if (!exerciseId || !accountCode) return;
    if (exerciseDetailById.has(exerciseId)) return;
    if (fetchingDetailIds.has(exerciseId)) return;
    setFetchingDetailIds((prev) => {
      const next = new Set(prev);
      next.add(exerciseId);
      return next;
    });
    axiosAuthed
      .get(`/accounts/${accountCode}/exercises/${exerciseId}`)
      .then((res) => {
        setExerciseDetailById((prev) => {
          const next = new Map(prev);
          next.set(exerciseId, res.data);
          return next;
        });
        // Warm the browser image cache so the popover doesn't flash the
        // skeleton on its first show. No-op for non-YouTube URLs.
        const url = res.data?.url || res.data?.videoUrl;
        const thumb = getYoutubeThumb(url);
        if (thumb) {
          const img = new Image();
          img.src = thumb;
        }
      })
      .catch(() => { /* swallow — popover falls back to list fields */ })
      .finally(() => {
        setFetchingDetailIds((prev) => {
          const next = new Set(prev);
          next.delete(exerciseId);
          return next;
        });
      });
  }, [accountCode, exerciseDetailById, fetchingDetailIds]);

  // Prefetch detail for every matched exercise once the list is loaded. Fires
  // once per session (guarded by ref) so it doesn't re-run when the cache
  // fills in. N roughly-parallel requests — scales with program size, not
  // with the full exercise library.
  const didPrefetchDetailsRef = useRef(false);
  useEffect(() => {
    if (didPrefetchDetailsRef.current) return;
    if (!accountCode || matches.length === 0 || exerciseById.size === 0) return;
    didPrefetchDetailsRef.current = true;
    const seen = new Set();
    matches.forEach((m) => {
      const id = m.selectedExerciseId;
      if (id == null || seen.has(id)) return;
      seen.add(id);
      fetchExerciseDetail(id);
    });
  }, [accountCode, matches, exerciseById, fetchExerciseDetail]);

  // Hover handlers with a small close-delay so the user can slide the cursor
  // from the info icon into the popover without it disappearing. Kicks off
  // the detail fetch for the hovered row's matched exercise.
  const openInfo = useCallback((key, exerciseId) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredInfoKey(key);
    if (exerciseId != null) fetchExerciseDetail(exerciseId);
  }, [fetchExerciseDetail]);

  const scheduleCloseInfo = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredInfoKey(null);
      hoverTimeoutRef.current = null;
    }, 180);
  }, []);

  useEffect(() => () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  }, []);

  // Whenever the hovered row changes, stop inline playback — otherwise the
  // next popover would mount with a stale "playing" state for its own row
  // (or, worse, display an iframe for a different exercise entirely).
  useEffect(() => {
    setPlayingVideoKey(null);
  }, [hoveredInfoKey]);

  // Close the inline editor when clicking outside of it. The react-select menu
  // is portaled to document.body, so we whitelist its class prefix too —
  // otherwise clicking "Create new exercise" in the menu would kill the editor.
  useEffect(() => {
    if (!activeEditKey) return undefined;
    const handleClickOutside = (e) => {
      const { target } = e;
      const rowEl = document.querySelector(
        `[data-extracted-name="${CSS.escape(activeEditKey)}"]`,
      );
      if (rowEl && rowEl.contains(target)) return;
      if (target.closest && target.closest('.ai-import-select__menu')) return;
      if (target.closest && target.closest('[data-ai-import-create-panel]')) return;
      setActiveEditKey(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeEditKey]);

  const unresolvedCount = useMemo(
    () => matches.filter((m) => m.selectedExerciseId == null).length,
    [matches],
  );

  // Seed the local draft from redux exactly once — when selectedProgram
  // first becomes truthy (it may hydrate async on direct landings into the
  // resume flow). Keying off `!programNameDraft` like the old effect did
  // would race the user's backspace: the moment they cleared the field to
  // empty, the effect would refill it from redux, making the last
  // character impossible to delete.
  const hasSeededDraftRef = useRef(Boolean(selectedProgram));
  useEffect(() => {
    if (!hasSeededDraftRef.current && selectedProgram) {
      setProgramNameDraft(selectedProgram);
      hasSeededDraftRef.current = true;
    }
  }, [selectedProgram]);

  const commitProgramName = useCallback(() => {
    const trimmed = programNameDraft.trim();
    // Empty draft is allowed (the coach is still typing or has just cleared
    // the field). We don't push that to redux — IMPORT PROGRAM stays
    // disabled until they enter something — but we also don't snap-revert
    // the input back to the previous value, which would steal their cursor.
    if (!trimmed) return;
    if (trimmed !== selectedProgram) {
      dispatch(setSelectedProgram(trimmed));
    }
  }, [dispatch, programNameDraft, selectedProgram]);

  const handleDone = useCallback(async () => {
    if (unresolvedCount > 0 || isConfirming) return;
    // Belt-and-suspenders against an empty program name reaching the
    // backend (the button is disabled in this case, but a stale render or
    // keyboard shortcut could in theory bypass that guard).
    if (!programNameDraft.trim()) return;
    // Commit any in-progress name edit before dispatching the confirm so
    // the action picks up the latest value from redux.
    commitProgramName();
    const result = await dispatch(confirmImport());
    if (result?.success) {
      const createdProgram = result.created?.[0];
      if (createdProgram?.id) {
        if (flags?.aiImportHomeLanding) {
          // Home-landing path (gated): stash the id so the Navigator can
          // render the one-time success banner above the program detail,
          // then route through the Navigator surface. This is the
          // intended long-term flow once the new Home ships.
          dispatch(setLastImportedProgramId(createdProgram.id));
          history.push(`/dashboard/programs/${createdProgram.id}`);
        } else {
          // Default (Build-landing) path: replicate ProgramNavigator's
          // "Edit Program" sequence so the wizard ships without
          // depending on the new Home Navigator. The Build page's
          // AIImportSuccessModal (keyed off lastImportedProgramId, same as
          // the Navigator banner) gives the coach explicit "Import
          // complete!" feedback here.
          //
          // Normalize the program shape before dispatching. The confirm
          // endpoint's `created[]` items don't guarantee a `programName`
          // field (the Navigator's programs slice has it because that
          // slice runs a separate fetch + normalize step), but the Build
          // header reads exactly that field — without normalizing the
          // breadcrumb falls back to "Select Program" even though the
          // workout content loads fine. The user-confirmed
          // programNameDraft is the most authoritative source (they just
          // typed/confirmed it on this screen), with the API shape's
          // possible aliases as fallbacks.
          const normalizedProgram = {
            ...createdProgram,
            id: createdProgram.id,
            programName: createdProgram.programName
              || createdProgram.name
              || programNameDraft.trim()
              || 'Imported program',
          };
          dispatch(setProgramType('free-form-program'));
          dispatch(setSelectedReduxNonDateBasedProgram(normalizedProgram));
          dispatch(setLastImportedProgramId(createdProgram.id));
          history.push('/build');
        }
      } else {
        // No created id back from the server — fall back to the wizard
        // entry so the coach isn't stranded on a dead Review screen.
        history.push('/import/program');
      }
    }
  }, [
    dispatch, history, unresolvedCount, isConfirming,
    commitProgramName, programNameDraft, flags,
  ]);

  const handleCancelImport = useCallback(() => {
    dispatch(resetImport());
    history.push('/import/program');
  }, [dispatch, history]);

  const handleRowClick = useCallback((key) => {
    if (isConfirming) return;
    setActiveEditKey((prev) => (prev === key ? null : key));
  }, [isConfirming]);

  const handleCancel = useCallback(() => {
    setActiveEditKey(null);
  }, []);

  // Mark a row as updated (persistent yellow + pill) and trigger a brief
  // brighter pulse so the coach sees the change land where they were already
  // looking. Re-running on the same row re-plays the pulse via the counter.
  const flashRow = useCallback((key) => {
    setUpdateCountByKey((prev) => {
      const next = new Map(prev);
      next.set(key, (prev.get(key) || 0) + 1);
      return next;
    });
    setFlashingKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    if (flashTimeoutsRef.current[key]) {
      clearTimeout(flashTimeoutsRef.current[key]);
    }
    flashTimeoutsRef.current[key] = setTimeout(() => {
      setFlashingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      delete flashTimeoutsRef.current[key];
    }, 700);
  }, []);

  useEffect(() => () => {
    Object.values(flashTimeoutsRef.current).forEach(clearTimeout);
    flashTimeoutsRef.current = {};
  }, []);

  const handleSelect = useCallback((extractedName, selectedOption) => {
    dispatch(selectExerciseForMatch(extractedName, selectedOption.value, selectedOption.label));
    flashRow(extractedName);
    setActiveEditKey(null);
  }, [dispatch, flashRow]);

  // Open the build-module's full Create Exercise modal pre-filled with the
  // extracted name as the exercise name. Stash the row key so the success
  // handler knows which match to update.
  const handleOpenCreateModal = useCallback((extractedName) => {
    // Close the inline editor first — its react-select menu is portaled to
    // document.body with z-index 9999, which would otherwise float above the
    // modal (whose overlay sits at 1100).
    setActiveEditKey(null);
    setCreateModalKey(extractedName);
    // The form pulls its initial name from `exercisePickerSearchTerm` when
    // modalData.openedFromExercisePicker is set — re-using that field keeps
    // the form code unchanged for our flow.
    dispatch(setExercisePickerSearchTerm(extractedName));
    dispatch(setActiveModal('createNewComponentModal', {
      type: 'L',
      openedFromExercisePicker: true,
      // AI import mirrors the BE, which defaults new exercises to save-to-database.
      // The build-module's own create flow keeps its original (unchecked) default.
      openedFromAiImport: true,
    }));
  }, [dispatch]);

  const handleCloseCreateModal = useCallback(() => {
    dispatch(setActiveModal('', null));
    dispatch(setExercisePickerSearchTerm(''));
    setCreateModalKey(null);
  }, [dispatch]);

  // Called by CreateNewExerciseForm with the freshly-created exercise.
  // We swap it into the AI import mapping for the row that opened the modal,
  // refresh the local options list so the new exercise is selectable in
  // other rows, and pulse the row to confirm the change.
  const handleExerciseCreated = useCallback((response) => {
    const key = createModalKey;
    if (!key || !response) return;
    dispatch(selectExerciseForMatch(key, response.id, response.name, { isNew: true }));
    if (exerciseOptionsRef.current) {
      const newOpt = { value: response.id, label: response.name };
      exerciseOptionsRef.current.push(newOpt);
      setExerciseOptions([...exerciseOptionsRef.current]);
    }
    setExerciseById((prev) => {
      const next = new Map(prev);
      next.set(response.id, response);
      return next;
    });
    flashRow(key);
    setActiveEditKey(null);
  }, [createModalKey, dispatch, flashRow]);

  // Track viewport width so the modal width can match the build module's
  // responsive sizing (75vw on desktop, 95vw under 600px).
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // The Import button blocks on three things: unresolved matches, an
  // in-flight confirm, or an empty program name. We surface the most
  // informative of the three in the button label so the coach knows what
  // to fix \u2014 "Name your program" trumps the unmatched count because the
  // name field is right above and fast to action.
  const isProgramNameBlank = !programNameDraft.trim();
  let doneLabel = 'Import program';
  if (isConfirming) {
    doneLabel = 'Importing\u2026';
  } else if (isProgramNameBlank) {
    doneLabel = 'Name your program';
  } else if (unresolvedCount > 0) {
    doneLabel = `${unresolvedCount} unmatched`;
  }

  return (
    <OuterWrapper>
      <HeaderBlock>
        <TitleBlock>
          <Title>Review and correct</Title>
          <ProgramNameField>
            <ProgramNameLabel htmlFor='ai-import-program-name'>Program Name</ProgramNameLabel>
            <ProgramNameInput
              id='ai-import-program-name'
              type='text'
              value={programNameDraft}
              onChange={(e) => setProgramNameDraft(e.target.value)}
              onBlur={commitProgramName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.target.blur();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setProgramNameDraft(selectedProgram || '');
                  e.target.blur();
                }
              }}
              placeholder='Name this program'
            />
          </ProgramNameField>
        </TitleBlock>

        <ExercisesBlock>
          <ExercisesIntro>
            <ExercisesHeading as='h2'>Exercises</ExercisesHeading>
            <ExercisesBody>
              It’s important to set up your exercises correctly. Exercises determine
              what your athletes can enter and how the results are tracked.
            </ExercisesBody>
          </ExercisesIntro>
          <ExercisesBody>
            Any exercise that wasn’t a perfect match is marked below. Choose exercises
            from our library or use the option to create one yourself. Check them using
            the preview button.
          </ExercisesBody>
        </ExercisesBlock>
      </HeaderBlock>

      {confirmError && (
        <ErrorBar>
          <IcomoonReact iconSet={iconSet} size={14} icon='warning' color='#C43D32' />
          <ErrorText>
            <strong>Import failed.</strong>
            {' '}
            {confirmError}
          </ErrorText>
        </ErrorBar>
      )}

      {optionsError && (
        <ErrorBar>
          <IcomoonReact iconSet={iconSet} size={14} icon='warning' color='#C43D32' />
          <ErrorText>
            <strong>Couldn’t load your exercise list.</strong>
            {' '}
            The match dropdown is unavailable — retry to pick exercises.
          </ErrorText>
          <RetryButton
            type='button'
            onClick={loadExerciseOptions}
            disabled={isLoadingOptions}
          >
            {isLoadingOptions ? 'Retrying…' : 'Retry'}
          </RetryButton>
        </ErrorBar>
      )}

      <TableWrap>
        <ListHeader>
          <HeaderLabel>Detected</HeaderLabel>
          <HeaderLabel>Matched to</HeaderLabel>
          <HeaderLabel>{' '}</HeaderLabel>
          <HeaderLabelRight>{' '}</HeaderLabelRight>
        </ListHeader>
        <List>
          {matches.map((match) => {
            const key = match.extractedName;
            const isActive = activeEditKey === key;
            const isFlashing = flashingKeys.has(key);
            const pulseId = updateCountByKey.get(key) || 0;
            const isUpdated = pulseId > 0;
            const isUnresolved = match.selectedExerciseId == null;
            // Yellow row tint reads the same as the "Check this" pill — both
            // signal that the row hasn't been confirmed. Once the coach
            // corrects it, the tint clears and the row gets the green
            // "Corrected" badge on the right.
            const isLowConfidence = match.matchConfidence != null
              && match.matchConfidence < LOW_CONFIDENCE_THRESHOLD;
            const needsAttentionNow = !isUpdated && (isUnresolved || isLowConfidence);
            const editorExercise = {
              id: key,
              rawName: match.extractedName,
              name: match.selectedExerciseName || '',
              isNew: isUnresolved,
            };
            // Pull the preview popover out of the matched-to cell so the
            // PREVIEW button can live in its own grid column.
            // We still need a single ExerciseInfoPreview per row, so build the
            // node here and reuse it inside the row markup below.
            const exerciseId = match.selectedExerciseId;
            const isInfoOpen = hoveredInfoKey === key;
            return (
              <Row
                key={key}
                isActive={isActive}
                isFlashing={isFlashing}
                needsAttention={needsAttentionNow}
                data-extracted-name={key}
                onClick={() => handleRowClick(key)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRowClick(key);
                  } else if (e.key === 'Escape' && isActive) {
                    setActiveEditKey(null);
                  }
                }}
              >
                <SourceCell>
                  <ExtractedName title={match.extractedName}>
                    {match.extractedName}
                  </ExtractedName>
                </SourceCell>
                <MatchedCell
                  isActive={isActive}
                  onClick={isActive ? (e) => e.stopPropagation() : undefined}
                  onKeyDown={isActive ? (e) => e.stopPropagation() : undefined}
                >
                  {isActive ? (
                    <InlineExerciseEditor
                      exercise={editorExercise}
                      exerciseOptions={exerciseOptions}
                      isLoadingOptions={isLoadingOptions}
                      onSwap={(_id, option) => handleSelect(key, option)}
                      onCreateClick={() => handleOpenCreateModal(key)}
                      onCancel={handleCancel}
                    />
                  ) : (
                    <>
                      <MatchedName
                        unresolved={isUnresolved}
                        title={match.selectedExerciseName || 'Select an exercise'}
                      >
                        {match.selectedExerciseName || 'Select an exercise'}
                      </MatchedName>
                      <MatchedChevron aria-hidden='true'>
                        <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                          <path
                            d='M7 10 L12 15 L17 10'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </MatchedChevron>
                    </>
                  )}
                </MatchedCell>
                <PreviewCell onClick={(e) => e.stopPropagation()}>
                  {!isUnresolved && !isActive && (
                    <ExerciseInfoPreview
                      isOpen={isInfoOpen}
                      onOpen={() => openInfo(key, exerciseId)}
                      onClose={scheduleCloseInfo}
                      onToggleClick={(e) => {
                        e.stopPropagation();
                        if (isInfoOpen) setHoveredInfoKey(null);
                        else openInfo(key, exerciseId);
                      }}
                    >
                      {isInfoOpen && (() => {
                        // Merge list record (lean — id/name/type/
                        // track/link) with lazy-fetched detail record
                        // (url, description, tags, full
                        // defaultOptions). Detail wins where they
                        // overlap, but list fields still show while
                        // detail is loading. Computed inside the open
                        // branch so closed rows skip the spread + Map
                        // lookups on every parent re-render.
                        const listEx = exerciseById.get(exerciseId);
                        const detailEx = exerciseDetailById.get(exerciseId);
                        const ex = { ...(listEx || {}), ...(detailEx || {}) };
                        const hasDetail = Boolean(detailEx);
                        const isFetchingDetail = fetchingDetailIds.has(exerciseId);
                        const videoUrl = ex?.url || ex?.videoUrl || null;
                        const thumb = getYoutubeThumb(videoUrl);
                        const maxInfo = formatMaxInfo(ex);
                        // "None" and "N/A" both read as "no max set"
                        // and get muted styling on the value.
                        const isMaxInfoMuted = maxInfo === 'None' || maxInfo === 'N/A';
                        const typeLabel = getTypeLabel(ex?.type);
                        const typeTone = TYPE_TONES[ex?.type] || TYPE_TONES.L;
                        const description = (ex?.description || '').trim();
                        const tagNames = Array.isArray(ex?.tags)
                          ? ex.tags
                            .map((t) => (typeof t === 'string' ? t : t?.name))
                            .filter(Boolean)
                          : [];
                        const trackingFlags = getTrackingFlags(ex);
                        const linkedName = ex?.linkTo
                          ? (exerciseById.get(ex.linkTo)?.name
                                  || exerciseDetailById.get(ex.linkTo)?.name
                                  || `#${ex.linkTo}`)
                          : null;
                        const linkedLabel = linkedName
                          ? `${linkedName}${ex.linkPercentage ? ` · ${ex.linkPercentage}%` : ''}`
                          : null;
                        const hasMeta = Boolean(linkedLabel)
                                || tagNames.length > 0
                                || trackingFlags.length > 0;
                        // The designation row (type chip + Max Info)
                        // always renders now, so "bare" only means we
                        // have nothing substantive below the header.
                        const isBare = hasDetail
                                && !videoUrl
                                && !description
                                && !hasMeta;
                        return (
                          <>
                            {videoUrl && playingVideoKey === key && (() => {
                              const ytId = getYoutubeId(videoUrl);
                              // YouTube URLs switch to an autoplay
                              // iframe embed with chrome stripped
                              // (modestbranding, rel=0). Uploaded
                              // files get a native HTML5 player with
                              // controls + autoplay. In both cases
                              // the source stays in the popover so
                              // the coach never leaves the import.
                              if (ytId) {
                                return (
                                  <PlayingSlot>
                                    <PopoverIframe
                                      src={`https://www.youtube.com/embed/${ytId}?autoplay=1&modestbranding=1&rel=0&playsinline=1`}
                                      title={match.selectedExerciseName}
                                      allow='autoplay; encrypted-media; picture-in-picture'
                                      allowFullScreen
                                    />
                                  </PlayingSlot>
                                );
                              }
                              return (
                                <PlayingSlot>
                                  <PopoverPlayingVideo
                                    src={videoUrl}
                                    controls
                                    autoPlay
                                    playsInline
                                  />
                                </PlayingSlot>
                              );
                            })()}
                            {videoUrl && playingVideoKey !== key && (
                            <PopoverVideo
                              href={videoUrl}
                              target='_blank'
                              rel='noopener noreferrer'
                              thumb={thumb}
                                      // Left-click plays inline; middle/
                                      // right-click still honour the href
                                      // so power users keep "open in new
                                      // tab" as a fallback.
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setPlayingVideoKey(key);
                              }}
                            >
                              {!thumb && (
                              <PopoverVideoEl
                                src={`${videoUrl}#t=0.1`}
                                preload='metadata'
                                muted
                                playsInline
                              />
                              )}
                              <PlayBadge />
                            </PopoverVideo>
                            )}
                            {!videoUrl && isFetchingDetail && (
                            <PopoverVideoSkeleton />
                            )}
                            <PopoverBody>
                              <PopoverHead>
                                <PopoverName as='h3' title={match.selectedExerciseName}>
                                  {match.selectedExerciseName}
                                </PopoverName>
                              </PopoverHead>
                              <DesignationRow>
                                {typeLabel && (
                                <TypeChip tone={typeTone}>{typeLabel}</TypeChip>
                                )}
                                <MaxInfoBadge>
                                  <MaxInfoLabel>Max Info</MaxInfoLabel>
                                  <MaxInfoValue muted={isMaxInfoMuted}>
                                    {maxInfo}
                                  </MaxInfoValue>
                                </MaxInfoBadge>
                              </DesignationRow>
                              {description && (
                              <PopoverDescription>{description}</PopoverDescription>
                              )}
                              {hasMeta && (
                              <MetaBlock>
                                {trackingFlags.length > 0 && (
                                <MetaRow>
                                  <MetaKey>Tracks</MetaKey>
                                  <TagList>
                                    {trackingFlags.map((flag) => (
                                      <TagChip key={flag}>{flag}</TagChip>
                                    ))}
                                  </TagList>
                                </MetaRow>
                                )}
                                {linkedLabel && (
                                <MetaRow>
                                  <MetaKey>Linked</MetaKey>
                                  <MetaValue title={linkedLabel}>{linkedLabel}</MetaValue>
                                </MetaRow>
                                )}
                                {tagNames.length > 0 && (
                                <MetaRow>
                                  <MetaKey>Tags</MetaKey>
                                  <TagList>
                                    {tagNames.map((tn) => (
                                      <TagChip key={tn}>{tn}</TagChip>
                                    ))}
                                  </TagList>
                                </MetaRow>
                                )}
                              </MetaBlock>
                              )}
                              {!hasDetail && isFetchingDetail && !description && !hasMeta && (
                              <PopoverEmpty>Loading details…</PopoverEmpty>
                              )}
                              {isBare && (
                              <PopoverEmpty>
                                No extra details on file for this exercise.
                              </PopoverEmpty>
                              )}
                            </PopoverBody>
                          </>
                        );
                      })()}
                    </ExerciseInfoPreview>
                  )}
                </PreviewCell>
                <ConfidenceCell>
                  {isActive ? null : (() => {
                    // Three signal states, in priority order:
                    //   1. Unresolved → red "No match" pill (we don't have a
                    //      match yet, so the coach MUST pick one before this
                    //      import is shippable).
                    //   2. Low-confidence match → yellow "Check this" pill
                    //      (we matched, but we're not confident).
                    //   3. Corrected → green "Corrected" badge once an
                    //      unresolved or low-confidence row has been touched.
                    if (isUnresolved) {
                      return (
                        <NoMatchPill title='No match yet — pick or create an exercise'>
                          No match
                        </NoMatchPill>
                      );
                    }
                    const needsAttention = match.matchConfidence != null
                      && match.matchConfidence < LOW_CONFIDENCE_THRESHOLD;
                    if (isUpdated && needsAttention) {
                      return <UpdatedBadge key={pulseId}>Corrected</UpdatedBadge>;
                    }
                    if (!isUpdated && needsAttention) {
                      return (
                        <CheckThisPill
                          title={match.matchConfidenceString
                            ? `${match.matchConfidenceString} match`
                            : 'Verify this match'}
                        >
                          Check this
                        </CheckThisPill>
                      );
                    }
                    return null;
                  })()}
                </ConfidenceCell>
              </Row>
            );
          })}
        </List>
      </TableWrap>

      <ActionBar>
        <CancelButton onClick={handleCancelImport} type='button'>
          Cancel
        </CancelButton>
        <PrimaryButton
          type='button'
          onClick={handleDone}
          disabled={
            unresolvedCount > 0
            || isConfirming
            || matches.length === 0
            || isProgramNameBlank
          }
        >
          {doneLabel}
          {!isConfirming && unresolvedCount === 0 && !isProgramNameBlank && (
            <ArrowGlyph>&rarr;</ArrowGlyph>
          )}
        </PrimaryButton>
      </ActionBar>

      {/* Reuse the build-module's full Create Exercise modal here. The form's
          calendar-side effects (auto-save, pre-save items) are bypassed via
          onSuccessOverride — we only need the created exercise back so we can
          swap it into the AI import mapping. Width/padding mirror the values
          the calendar's CalendarModalHandler picks for this same modal, and
          the 100%-wide wrapper matches what CalendarModalHandler renders so
          the form's ResizeObserver measures the full modal width — without
          it, BaseModal's flex content collapses the inner ModalContainer to
          content size and Circuit / SAQ+C drop to their narrow layouts. */}
      <BaseModal
        isOpen={createModalKey !== null}
        visuallyHidden={isSimilarCheckOpen}
        // While the similar-exercises dialog is up this modal is hidden but
        // still mounted — Escape/backdrop must not tear down the form under it.
        onRequestClose={() => { if (!isSimilarCheckOpen) handleCloseCreateModal(); }}
        width={windowWidth < 600 ? '95vw' : '75vw'}
        padding='24px'
        minWidth='330px'
      >
        <CreateModalWrapper>
          <CreateNewComponentModal
            closeModal={handleCloseCreateModal}
            onSuccessOverride={handleExerciseCreated}
            onSimilarCheckOpenChange={setIsSimilarCheckOpen}
          />
        </CreateModalWrapper>
      </BaseModal>
    </OuterWrapper>
  );
};

StepExerciseChecker.propTypes = {
  // Injected by withLDConsumer below. Only `flags.aiImportHomeLanding`
  // is read — gates the post-import landing surface. Optional so
  // unit-test mounts without an LDProvider don't crash.
  flags: PropTypes.shape({
    aiImportHomeLanding: PropTypes.bool,
  }),
};

StepExerciseChecker.defaultProps = {
  flags: {},
};

export default withLDConsumer()(StepExerciseChecker);
